import {
  CloseAction,
  CloseActionType,
} from "@redotech/redo-model/content-builder/element-schema";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { assertNever } from "@redotech/util/type";

function getShopifyRoutes(): string {
  return (globalThis as any).Shopify?.routes?.root ?? "/";
}

/**
 * Applies a discount code to the Shopify cart using the Ajax API.
 */
async function applyDiscountToCart(discountCode: string): Promise<void> {
  const response = await fetch(`${getShopifyRoutes()}cart/update.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ discount: discountCode }),
  });
  if (!response.ok) {
    console.error("Failed to apply discount to cart", await response.text());
  }
}

/**
 * Navigates to the Shopify cart page.
 */
function openCart(): void {
  window.location.href = `${window.location.origin}/cart`;
}

/**
 * Navigates to the Shopify checkout page.
 */
function openCheckout(): void {
  window.location.href = `${window.location.origin}/checkout`;
}

/**
 * Adds a product variant to the Shopify cart using the Ajax API.
 */
async function addProductToCart(
  variantId: string,
  quantity: number,
  properties?: Record<string, string>,
): Promise<void> {
  const numericId = parseNumericIdFromShopifyId(variantId);
  const response = await fetch(`${getShopifyRoutes()}cart/add.js`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items: [{ id: Number(numericId), quantity, properties }],
    }),
  });
  if (!response.ok) {
    console.error("Failed to add product to cart", await response.text());
  }
}

export interface CloseActionContext {
  /** Map of discountId -> discountCode for resolved button action discounts */
  resolvedDiscounts?: Record<string, string>;
  /** Subtext to show below buttons that navigate to checkout (e.g. "(Checkout+ included)") */
  buttonSubtext?: string;
  /** Callback to run before navigating to checkout (e.g. add coverage product to cart, update cart attributes) */
  onCheckoutAction?: () => Promise<void>;
}

/**
 * Executes a single close action.
 */
async function executeCloseAction(
  action: CloseAction,
  context: CloseActionContext,
): Promise<void> {
  switch (action.type) {
    case CloseActionType.ADD_DISCOUNT_TO_CART: {
      const discountCode = context.resolvedDiscounts?.[action.discountId];
      if (discountCode) {
        await applyDiscountToCart(discountCode);
      } else {
        console.warn(
          "ADD_DISCOUNT_TO_CART action triggered but no discount code available for discountId:",
          action.discountId,
        );
      }
      break;
    }
    case CloseActionType.OPEN_CART:
      openCart();
      break;
    case CloseActionType.OPEN_CHECKOUT:
      if (context.onCheckoutAction) {
        await context.onCheckoutAction();
      }
      openCheckout();
      break;
    case CloseActionType.ADD_PRODUCT_TO_CART:
      if (action.productId && action.variantId) {
        await addProductToCart(action.variantId, action.quantity ?? 1);
      } else {
        console.warn(
          "ADD_PRODUCT_TO_CART action triggered but no variantId available",
          action,
        );
      }
      break;
    default:
      assertNever(action);
  }
}

/**
 * Executes an array of close actions in sequence.
 * Navigation actions (OPEN_CART, OPEN_CHECKOUT) should be last since they redirect.
 */
export async function executeCloseActions(
  actions: CloseAction[],
  context: CloseActionContext,
): Promise<void> {
  // Sort actions so navigation happens last (since it redirects the page)
  const sortedActions = [...actions].sort((a, b) => {
    const isNavigationA =
      a.type === CloseActionType.OPEN_CART ||
      a.type === CloseActionType.OPEN_CHECKOUT;
    const isNavigationB =
      b.type === CloseActionType.OPEN_CART ||
      b.type === CloseActionType.OPEN_CHECKOUT;
    if (isNavigationA && !isNavigationB) {
      return 1;
    }
    if (!isNavigationA && isNavigationB) {
      return -1;
    }
    return 0;
  });

  for (const action of sortedActions) {
    await executeCloseAction(action, context);
  }
}
